import React, { useRef, useState } from 'react'
import { Download, Upload } from 'lucide-react'
import { projectsApi } from '../../api/projects'
import { Button } from '../common/Button'

interface IssueSheetActionsProps {
  projectId: string
  projectIdentifier?: string
  canImport?: boolean
}

function suggestedFilename(projectIdentifier?: string): string {
  const date = new Date().toISOString().slice(0, 10)
  return `${(projectIdentifier || 'project').toLowerCase()}-issues-${date}.csv`
}

export default function IssueSheetActions({
  projectId,
  projectIdentifier,
  canImport = true,
}: IssueSheetActionsProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [exporting, setExporting] = useState(false)
  const [importing, setImporting] = useState(false)

  const handleExport = async () => {
    try {
      setExporting(true)
      const response = await projectsApi.exportIssuesSheet(projectId)
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = suggestedFilename(projectIdentifier)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch {
      window.alert('Could not export the issue sheet right now.')
    } finally {
      setExporting(false)
    }
  }

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const fileName = file.name.toLowerCase()
    if (!fileName.endsWith('.csv')) {
      window.alert('Please upload a CSV issue sheet. Excel can open and save this format easily.')
      return
    }

    try {
      setImporting(true)
      const response = await projectsApi.importIssuesSheet(projectId, file)
      const result = response.data.data
      const warningText = result.warnings?.length
        ? `\n\nWarnings:\n- ${result.warnings.slice(0, 8).join('\n- ')}${result.warnings.length > 8 ? '\n- ...' : ''}`
        : ''
      window.alert(`Issue sheet imported.\nCreated: ${result.created}\nUpdated: ${result.updated}${warningText}`)
      window.location.reload()
    } catch (error: unknown) {
      const message =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
          ? (error as { response?: { data?: { error?: string } } }).response!.data!.error!
          : 'Could not import the issue sheet.'
      window.alert(message)
    } finally {
      setImporting(false)
    }
  }

  return (
    <>
      {canImport && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleImport}
        />
      )}
      <Button variant="secondary" size="sm" onClick={handleExport} loading={exporting}>
        <Download size={14} /> Export Sheet
      </Button>
      {canImport && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          loading={importing}
        >
          <Upload size={14} /> Import Sheet
        </Button>
      )}
    </>
  )
}
